import { FieldChangeReasonProvider } from '@/contexts/FieldChangeReasonContext';

import { TaskCanonicalGroupPills } from './components/TaskCanonicalGroupPills';
import { TaskMetricsBoxes } from './components/TaskMetricsBoxes';
import { VerticalConfig } from './config/taskVerticals';
import {
  ColumnsFactory,
  useTaskVerticalView,
} from './hooks/useTaskVerticalView';
import { TaskListView } from './TaskListView';

interface TaskVerticalViewProps {
  verticalConfig: VerticalConfig;
  columnsFactory: ColumnsFactory;
}

/**
 * TaskVerticalView - Reusable component for all task vertical views
 *
 * This component orchestrates the data fetching, filter preparation, and rendering
 * for any task vertical (Claims, Intake, Liability, etc.).
 *
 * It delegates:
 * - Data logic to useTaskVerticalView hook
 * - Column definition to the injected columnsFactory
 * - Layout rendering to TaskListView
 */
export const TaskVerticalView = ({
  verticalConfig,
  columnsFactory,
}: TaskVerticalViewProps) => {
  const {
    columns,
    advancedFiltersMetadata,
    staticTotalCount,
    staticMetadata,
    countsData,
  } = useTaskVerticalView({
    verticalConfig,
    columnsFactory,
  });

  return (
    <FieldChangeReasonProvider>
      <TaskListView
        verticalConfig={verticalConfig}
        columns={columns}
        advancedFiltersMetadata={advancedFiltersMetadata}
        // eslint-disable-next-line react/no-unstable-nested-components
        metricsComponent={({ paginationState, paginationActions }) => (
          <TaskMetricsBoxes
            paginationState={paginationState}
            paginationActions={paginationActions}
            totalCount={staticTotalCount}
            metadata={staticMetadata}
            verticalConfig={verticalConfig}
          />
        )}
        // eslint-disable-next-line react/no-unstable-nested-components
        filterPillsComponent={({ paginationState, paginationActions }) => (
          <TaskCanonicalGroupPills
            paginationState={paginationState}
            paginationActions={paginationActions}
            countsData={countsData}
          />
        )}
      />
    </FieldChangeReasonProvider>
  );
};
